import React, { FC, useState, useMemo } from 'react';
import { Box, Divider, TextField, Typography, Tab, Tabs, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { PermissionItem } from 'pages/web/userManagement/subpages/profiles/type';

interface DetailsProps {
    permissions: PermissionItem[];
    views: any[];
    tags?: any[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ flexGrow: 1, overflowY: 'auto', maxHeight: '400px' }}
        >
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const GroupedPermissions: FC<{ permissions: PermissionItem[] }> = ({ permissions }) => {
    // Aseguramos que permissions sea un array
    const permissionsArray = useMemo(() =>
        Array.isArray(permissions) ? permissions : [],
        [permissions]
    );

    const grouped = useMemo(() => {
        return permissionsArray.reduce((acc, perm) => {
            // Verificar que perm y perm.name existan
            if (!perm || !perm.name) return acc;

            const nameParts = perm.name.split('.');
            const group = nameParts[0];
            const name = nameParts.length > 1 ? nameParts[1] : perm.name;

            if (!acc[group]) acc[group] = [];
            acc[group].push({ ...perm, name: name });
            return acc;
        }, {} as Record<string, PermissionItem[]>);
    }, [permissionsArray]);

    return (
        <Box sx={{ width: '100%' }}>
            {Object.entries(grouped).map(([group, perms], index) => (
                <Box key={group} sx={{ mb: 2 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            mb: 1,
                            textTransform: 'lowercase',
                            "&::first-letter": {
                                textTransform: 'uppercase'
                            }
                        }}
                    >
                        {group}
                    </Typography>
                    <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} />

                    <Grid container spacing={1}>
                        {perms.map((perm) => (
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} key={perm.permission_id}>
                                <Box
                                    sx={{
                                        p: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        borderRadius: '4px',
                                        mb: 0.5
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'white',
                                            fontSize: '0.9rem',
                                            textTransform: 'lowercase',
                                            "&::first-letter": {
                                                textTransform: 'uppercase'
                                            }
                                        }}
                                    >
                                        {perm.name}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Box>
    );
};

const ViewList: FC<{ views: any[] }> = ({ views }) => {
    const { t } = useTranslation();
    // Aseguramos que views sea un array
    const viewsArray = Array.isArray(views) ? views : [];

    return (
        <Box sx={{ width: '100%' }}>
            <Typography
                variant="subtitle1"
                sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 2
                }}
            >
                {t('profiles.table.dialog.assign_views.views')}
            </Typography>

            <Grid container spacing={1}>
                {viewsArray.map((view) => (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} key={view?.view_id || Math.random()}>
                        <Box
                            sx={{
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                                mb: 0.5
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {view?.description || ''}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

const filterItems = <T extends { name?: string; description?: string }>(
    items: T[],
    searchValue: string
): T[] => {
    const validItems = Array.isArray(items) ? items : [];
    return validItems.filter(item =>
        (item?.name && item.name.toLowerCase().includes(searchValue.toLowerCase())) ||
        (item?.description && item.description.toLowerCase().includes(searchValue.toLowerCase()))
    );
};

const Details: FC<DetailsProps> = ({ permissions = [], views = [], tags = [] }) => {
    const [searchValue, setSearchValue] = useState('');
    const [value, setValue] = useState(0);
    const { t } = useTranslation();

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };


    const filteredPermissions = useMemo(() => filterItems(permissions, searchValue), [permissions, searchValue]);
    const filteredViews = useMemo(() => filterItems(views, searchValue), [views, searchValue]);




    return (
        <Box sx={{
            width: '100%',
            backgroundColor: '#2A2A2A',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="permission tabs"
                    sx={{
                        '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&.Mui-selected': {
                                color: '#fff'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#9c27b0'
                        }
                    }}
                >
                    <Tab label={t('profiles.table.dialog.info.permissions')} {...a11yProps(0)} />
                    <Tab label={t('profiles.table.dialog.info.views')} {...a11yProps(1)} />
                    <Tab disabled label={t('profiles.table.dialog.info.tags')} {...a11yProps(2)} />
                </Tabs>
            </Box>

            <Box sx={{ p: 2, flexShrink: 0 }}>
                <TextField
                    fullWidth
                    placeholder={t('profiles.table.dialog.info.search.search')}
                    variant="outlined"
                    value={searchValue}
                    autoComplete='off'
                    onChange={(e) => setSearchValue(e.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#9c27b0',
                            },
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                </Box>
                            ),
                        }

                    }}
                />
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <CustomTabPanel value={value} index={0}>
                    <GroupedPermissions permissions={filteredPermissions} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                    <ViewList views={filteredViews} />
                </CustomTabPanel>
            </Box>
        </Box>
    );
};

export { Details };